import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Edit, Trash2, DollarSign } from 'lucide-react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import {
  useFetchETFHoldings,
  useUpdateETFHolding,
  useDeleteETFHolding,
} from '@/hooks/securities/etf/useETFHoldings.ts';
import {
  useFetchETFDividends,
  useCreateETFDividend,
  useDeleteETFDividend,
} from '@etfSecurityHooks';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ETFForm } from '@/components/etf-form/ETFForm';
import { ETFDividendForm } from '@/components/etf-dividend-form/ETFDividendForm';
import { ETFMetricsCards } from '@/components/etf-metrics-cards/ETFMetricsCards';
import { ETFPriceTrendChart } from '@/components/etf-price-trend-chart/ETFPriceTrendChart';
import { ETFDividendHistoryTable } from '@/components/etf-dividend-history-table/ETFDividendHistoryTable';
import { PageHeaderSkeleton, AccountCardSkeleton, ChartSkeleton } from '@/components/LoadingSkeletons';
import {ETFHolding} from "@/integrations/supabase/etf.models.ts";

const TIME_PERIODS: TimeRange[] = ['1W', '1M', '1Y', 'ALL'];

const CATEGORY_LABELS: Record<string, string> = {
  equity: 'Equity',
  fixed_income: 'Fixed Income',
  commodity: 'Commodity',
  real_estate: 'Real Estate',
  multi_asset: 'Multi-Asset',
};

const FREQUENCY_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annual: 'Semi-Annual',
  annual: 'Annual',
};

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

/**
 * ETFDetailPage displays detailed information for a single ETF holding.
 * Accessible at /investments/securities/etfs/:holdingId
 */
export default function ETFDetailPage() {
  const { holdingId } = useParams<{ holdingId: string }>();
  const navigate = useNavigate();

  const [timePeriod, setTimePeriod] = useState<TimeRange>('ALL');
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dividendFormOpen, setDividendFormOpen] = useState(false);

  const { data: holdings = [], isLoading: holdingsLoading } = useFetchETFHoldings();
  const { data: dividends = [], isLoading: dividendsLoading } = useFetchETFDividends(holdingId);

  const updateHolding = useUpdateETFHolding();
  const deleteHolding = useDeleteETFHolding();
  const createDividend = useCreateETFDividend();
  const deleteDividend = useDeleteETFDividend();

  const holding: ETFHolding | undefined = useMemo(
    () => holdings.find((h) => h.id === holdingId),
    [holdings, holdingId]
  );

  const prices = useMemo(() => ({}), []);

  const isLoading = holdingsLoading || dividendsLoading;

  const handleSaveHolding = async (data: ETFHoldingCreateRequest) => {
    try {
      await updateHolding.mutateAsync({ id: holdingId!, ...data });
      toast.success('ETF holding updated');
      setEditFormOpen(false);
    } catch {
      toast.error('Failed to update ETF holding');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteHolding.mutateAsync(holdingId!);
      toast.success('ETF holding deleted');
      navigate('/investments/securities/etfs');
    } catch {
      toast.error('Failed to delete ETF holding');
      setDeleteDialogOpen(false);
    }
  };

  const handleSaveDividend = async (data: ETFDividendCreateRequest) => {
    try {
      await createDividend.mutateAsync(data);
      toast.success('Dividend recorded');
      setDividendFormOpen(false);
    } catch {
      toast.error('Failed to record dividend');
    }
  };

  const handleDeleteDividend = async (id: string) => {
    try {
      await deleteDividend.mutateAsync(id);
      toast.success('Dividend deleted');
    } catch {
      toast.error('Failed to delete dividend');
    }
  };

  if (isLoading) {
    return (
      <ErrorBoundary onReset={() => window.location.reload()}>
        <div className="space-y-6" aria-busy="true" aria-live="polite" data-testid="loading-skeleton">
          <PageHeaderSkeleton />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => <AccountCardSkeleton key={i} />)}
          </div>
          <AccountCardSkeleton />
          <ChartSkeleton />
        </div>
      </ErrorBoundary>
    );
  }

  if (!holding) {
    return (
      <ErrorBoundary onReset={() => window.location.reload()}>
        <div className="space-y-4" data-testid="not-found-state">
          <p className="text-muted-foreground">ETF holding not found.</p>
          <Button asChild variant="outline">
            <Link to="/investments/securities/etfs">
              <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
              Back to ETFs
            </Link>
          </Button>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary onReset={() => window.location.reload()}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/investments/securities/etfs')}
              aria-label="Back to ETFs overview"
              className="min-h-[44px] min-w-[44px]"
              data-testid="back-button"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                {holding.ticker_symbol}
              </h1>
              <p className="text-sm text-muted-foreground sm:text-base">{holding.etf_name}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2" role="group" aria-label="ETF holding actions">
            <Button
              variant="outline"
              onClick={() => setDividendFormOpen(true)}
              className="min-h-[44px]"
              aria-label="Record dividend"
            >
              <DollarSign className="mr-2 h-4 w-4" aria-hidden="true" />
              Record Dividend
            </Button>
            <Button
              variant="outline"
              onClick={() => setEditFormOpen(true)}
              className="min-h-[44px]"
              aria-label="Edit ETF holding"
            >
              <Edit className="mr-2 h-4 w-4" aria-hidden="true" />
              Edit
            </Button>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(true)}
              className="min-h-[44px]"
              aria-label="Delete ETF holding"
            >
              <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
              Delete
            </Button>
          </div>
        </div>

        {/* Metrics Cards */}
        <ETFMetricsCards holdings={[holding]} prices={prices} isLoading={false} />

        {/* Holding Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>Holding Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Ticker Symbol</p>
                <p className="font-medium">{holding.ticker_symbol}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">ETF Name</p>
                <p className="font-medium">{holding.etf_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Category</p>
                <Badge variant="secondary">
                  {CATEGORY_LABELS[holding.category] ?? holding.category}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Units Held</p>
                <p className="font-medium">
                  {holding.units_held.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 6,
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Avg Cost / Unit</p>
                <p className="font-medium">
                  {holding.currency}{' '}
                  {holding.average_cost_per_unit.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 4,
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Currency</p>
                <p className="font-medium">{holding.currency}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Purchase Date</p>
                <p className="font-medium">{formatDate(holding.purchase_date)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Dividend / Unit</p>
                <p className="font-medium">
                  {holding.dividend_per_unit.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 4,
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Dividend Frequency</p>
                <p className="font-medium">
                  {FREQUENCY_LABELS[holding.dividend_frequency] ?? holding.dividend_frequency}
                </p>
              </div>
              {holding.notes && (
                <div className="sm:col-span-2 lg:col-span-3">
                  <p className="text-sm text-muted-foreground mb-1">Notes</p>
                  <p className="font-medium">{holding.notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Price Trend Chart with time range selector */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base font-semibold">Price History</CardTitle>
            <div className="flex gap-1" role="group" aria-label="Time range selector">
              {TIME_PERIODS.map((period) => (
                <Button
                  key={period}
                  variant={timePeriod === period ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTimePeriod(period)}
                  aria-pressed={timePeriod === period}
                  className="min-h-[32px] px-3 text-xs"
                >
                  {period}
                </Button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <ETFPriceTrendChart tickerSymbol={holding.ticker_symbol} period={timePeriod} />
          </CardContent>
        </Card>

        {/* Dividend History Table */}
        <Card>
          <CardHeader>
            <CardTitle>Dividend History</CardTitle>
          </CardHeader>
          <CardContent>
            <ETFDividendHistoryTable
              dividends={dividends}
              onDelete={handleDeleteDividend}
              isDeleting={deleteDividend.isPending}
            />
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editFormOpen} onOpenChange={(open) => { setEditFormOpen(open); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>Edit ETF Holding</DialogTitle>
              <DialogDescription>Update the details of your ETF holding.</DialogDescription>
            </DialogHeader>
            <ETFForm
              holding={holding}
              onSave={handleSaveHolding}
              onCancel={() => setEditFormOpen(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => !open && setDeleteDialogOpen(false)}>
          <AlertDialogContent className="w-[95vw] sm:w-full sm:max-w-[500px]">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete ETF holding?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete <strong>{holding.ticker_symbol}</strong> and all
                associated dividend records. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
              <AlertDialogCancel
                onClick={() => setDeleteDialogOpen(false)}
                className="min-h-[44px] w-full sm:w-auto"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                className="min-h-[44px] w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Record Dividend Dialog */}
        <Dialog open={dividendFormOpen} onOpenChange={(open) => { setDividendFormOpen(open); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[500px] w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>Record Dividend</DialogTitle>
              <DialogDescription>Log a dividend payment for {holding.ticker_symbol}.</DialogDescription>
            </DialogHeader>
            <ETFDividendForm
              holdings={[holding]}
              preselectedHoldingId={holding.id}
              onSave={handleSaveDividend}
              onCancel={() => setDividendFormOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>
    </ErrorBoundary>
  );
}
